// Bilingual articles — source of truth for /<lang>/articles and detail pages.
// Body blocks: { type, text: {ar, en} | string } — `pickLang()` resolves them.
//
// Each article also carries SEO fields (`metaTitle`, `metaDescription`,
// `keywords`) consumed by `ArticleDetail` when rendering `<title>` and
// `<meta name="description">`.

export const ARTICLE_CATEGORIES = [
  { id: "fundamentals", label: { ar: "مفاهيم أساسية", en: "Fundamentals" } },
  { id: "tools", label: { ar: "الأدوات والتطبيقات", en: "Tools & applications" } },
  { id: "research", label: { ar: "البحث العلمي والتعلّم", en: "Research & learning" } },
  { id: "voice-english", label: { ar: "التحدث وممارسة الإنجليزية", en: "Voice & English practice" } },
  { id: "privacy", label: { ar: "الخصوصية والوصول الآمن", en: "Privacy & secure access" } },
];

export const getArticleCategory = (id) =>
  ARTICLE_CATEGORIES.find((c) => c.id === id) || null;

/* Article 1: What is AI / ما هو الذكاء الاصطناعي -------------------- */
const A1 = {
  slug: "what-is-ai-for-students-in-syria",
  category: "fundamentals",
  date: "2026-01-15",
  dateLabel: { ar: "كانون الثاني 2026", en: "January 2026" },
  readingTime: { ar: "5 دقائق", en: "5 min read" },
  coverVariant: "default",
  relatedTools: ["chatgpt-free", "google-gemini", "microsoft-copilot"],
  title: {
    ar: "ما هو الذكاء الاصطناعي وكيف يمكن للطالب في سوريا أن يبدأ؟",
    en: "What Is Artificial Intelligence and How Can a Student in Syria Start?",
  },
  excerpt: {
    ar: "دليل مبسّط يشرح معنى الذكاء الاصطناعي وأهم استخداماته العملية، وكيف يمكن للطالب في سوريا أن يبدأ التعلّم والاستفادة من أدوات مجانية بشكل تدريجي.",
    en: "A simple guide explaining what artificial intelligence means, how it is used in everyday learning, and how a student in Syria can begin with free practical tools.",
  },
  summary: {
    ar: "أصبح الذكاء الاصطناعي من أكثر المواضيع حضورًا في التعليم والعمل، لكنّه ما يزال غامضًا لدى كثير من الطلاب. نشرح في هذا المقال ببساطة ما هو الذكاء الاصطناعي، وما الفرق بينه وبين البرامج التقليدية، وكيف يمكن للطالب في سوريا أن يستفيد منه دون تكلفة كبيرة، مع التركيز على أدوات مجانية بسيطة مثل ChatGPT Free وGoogle Gemini.",
    en: "AI has become one of the most discussed topics in education and work, yet it still feels unclear to many students. This article explains in simple language what AI is, how it differs from traditional software, and how a student in Syria can begin using it without high cost — focusing on simple free tools such as ChatGPT Free and Google Gemini.",
  },
  metaTitle: {
    ar: "ما هو الذكاء الاصطناعي للطلاب في سوريا | SAS",
    en: "What Is Artificial Intelligence for Students in Syria | SAS",
  },
  metaDescription: {
    ar: "تعرّف على معنى الذكاء الاصطناعي وكيف يمكن للطلاب في سوريا البدء باستخدام أدوات ذكاء اصطناعي مجانية في الدراسة والتعلّم والبحث.",
    en: "Learn what artificial intelligence means and how students in Syria can start using free AI tools for study, learning, and everyday productivity.",
  },
  keywords: {
    ar: [
      "الذكاء الاصطناعي في سوريا",
      "ما هو الذكاء الاصطناعي",
      "تعلم الذكاء الاصطناعي في سوريا",
      "الذكاء الاصطناعي للطلاب السوريين",
      "أدوات الذكاء الاصطناعي المجانية",
    ],
    en: [
      "artificial intelligence in Syria",
      "what is artificial intelligence",
      "AI for students in Syria",
      "learning AI in Syria",
      "free AI tools",
    ],
  },
  body: [
    {
      type: "paragraph",
      text: {
        ar: "أصبح الذكاء الاصطناعي حاضرًا في كل مكان تقريبًا: في محرّكات البحث وتطبيقات الهاتف وأدوات الدراسة والعمل اليومي. ورغم ذلك، ما يزال كثير من الطلاب في سوريا يشعرون أنّ المفهوم نفسه غامض. يهدف هذا المقال إلى تقديم مدخل عمليّ قصير: ما هو الذكاء الاصطناعي بلغة بسيطة، وكيف يمكن البدء باستخدامه اليوم بأدوات مجانية ومناسبة.",
        en: "Artificial intelligence is now almost everywhere — in search engines, phone apps, and the tools we use to study and work. Even so, many students in Syria still feel the concept itself is fuzzy. This article is a short, practical entry point: what AI really means in simple language, and how to start using it today with free tools.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما المقصود بالذكاء الاصطناعي؟", en: "What does AI actually mean?" } },
    {
      type: "paragraph",
      text: {
        ar: "الذكاء الاصطناعي هو مجموعة من البرامج التي تتعلّم من كميات كبيرة من البيانات بدلًا من الاعتماد على قواعد ثابتة مكتوبة مسبقًا. هذا الفرق الجوهري هو ما يجعلها قادرة على الإجابة عن أسئلة مفتوحة، وصياغة نصوص جديدة، وتلخيص محتوى طويل بسرعة.",
        en: "AI is a family of programs that learn from large amounts of data instead of relying on fixed pre-written rules. That fundamental difference is what lets them answer open questions, write fresh text, and summarise long content quickly.",
      },
    },
    {
      type: "paragraph",
      text: {
        ar: "معظم ما يستخدمه الطالب يوميًا هو طبقة واحدة من هذا المجال: مساعدات المحادثة مثل ChatGPT وGoogle Gemini وMicrosoft Copilot. تستقبل هذه الأدوات نصًّا وتردّ بنصّ، وهي نقطة البداية الأسهل لمن يريد فهم الذكاء الاصطناعي من خلال التجربة.",
        en: "Most of what a student uses day-to-day is one layer of this field: conversational assistants such as ChatGPT, Google Gemini, and Microsoft Copilot. These tools take text and reply with text — the easiest entry point for understanding AI through direct practice.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف يفيد الطالب في الدراسة اليومية؟", en: "How can it help students every day?" } },
    {
      type: "paragraph",
      text: {
        ar: "يستطيع الطالب استخدام هذه الأدوات لشرح مفاهيم صعبة بلغة أبسط، وتلخيص محاضرات طويلة، وتحضير ملخّصات مراجعة سريعة، وتحسين الكتابة الأكاديمية، والإجابة عن أسئلة عامة قبل فتح المرجع الرسمي. كلّ ذلك يمكن إنجازه من خلال محادثة نصّية مباشرة.",
        en: "Students can use these tools to explain difficult concepts in simpler language, summarise long lectures, prepare quick revision notes, improve academic writing, and answer general questions before opening a formal reference — all through a simple text conversation.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما الأخطاء الشائعة عند استخدامه؟", en: "What mistakes should beginners avoid?" } },
    {
      type: "paragraph",
      text: {
        ar: "أكبر خطأ هو الثقة الكاملة في المخرجات دون مراجعة. قد تبدو الإجابات مقنعة لكنّها أحيانًا غير دقيقة أو غير محدّثة. الخطأ الثاني هو استخدام الأداة كبديل عن التعلّم الحقيقي، في حين أن دورها الفعلي هو تسريع الفهم، لا إلغاؤه.",
        en: "The biggest mistake is fully trusting outputs without reviewing them. Answers can sound convincing while being inaccurate or out of date. The second mistake is using the tool as a shortcut that replaces learning — its actual role is to speed up understanding, not bypass it.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما أفضل نقطة بداية للمبتدئ في سوريا؟", en: "What is the best starting point for a beginner in Syria?" } },
    {
      type: "paragraph",
      text: {
        ar: "ننصح بالبدء من أداة واحدة فقط: ChatGPT Free أو Google Gemini. جرّب استخدامها في ثلاث مهام حقيقية من دراستك هذا الأسبوع — شرح درس، وتلخيص فصل، ومراجعة نص كتبته — ثم وسّع استخدامك تدريجيًّا نحو أدوات أكثر تخصّصًا.",
        en: "Start with just one tool: ChatGPT Free or Google Gemini. Use it on three real tasks from your studies this week — explaining a lesson, summarising a chapter, and reviewing something you wrote — then gradually expand to more specialised tools.",
      },
    },
    {
      type: "takeaway",
      text: {
        ar: "الذكاء الاصطناعي ليس تقنية بعيدة ولا مادة دراسية مغلقة. هو أداة عمليّة يمكن للطالب السوري استخدامها اليوم، ابتداءً من سؤال بسيط وانتهاءً بدعم يومي لدراسته.",
        en: "AI is neither a distant technology nor a closed academic subject. It is a practical tool a Syrian student can use today — starting from a single question and growing into daily study support.",
      },
    },
    {
      type: "paragraph",
      text: {
        ar: "لاستكشاف مزيد من الأدوات المجانية المناسبة للطلاب والباحثين، يمكنك تصفّح دليل الأدوات في SAS.",
        en: "To explore more free tools suited for students and researchers, browse the SAS tools directory.",
      },
    },
    {
      type: "link",
      href: "/tools",
      text: { ar: "دليل الأدوات المجانية", en: "Free tools directory" },
      description: {
        ar: "الأدوات التي نرشّحها للطلاب والمبتدئين.",
        en: "The tools we recommend for students and beginners.",
      },
    },
  ],
};

/* Article 2: Best free AI tools / أفضل الأدوات المجانية -------------- */
const A2 = {
  slug: "best-free-ai-tools-in-syria",
  category: "tools",
  date: "2026-01-25",
  dateLabel: { ar: "كانون الثاني 2026", en: "January 2026" },
  readingTime: { ar: "6 دقائق", en: "6 min read" },
  coverVariant: "presentations",
  relatedTools: ["chatgpt-free", "google-gemini", "kimi", "deepseek", "perplexity", "notebooklm", "gamma"],
  title: {
    ar: "أفضل أدوات الذكاء الاصطناعي المجانية في سوريا للطلاب والمبتدئين",
    en: "Best Free AI Tools in Syria for Students and Beginners",
  },
  excerpt: {
    ar: "مقال يجمع أفضل أدوات الذكاء الاصطناعي المجانية أو ذات الخطط المجانية العملية التي يمكن استخدامها في سوريا للتعلّم والشرح والبحث والكتابة.",
    en: "A practical guide to the best free AI tools and useful free-tier platforms that can be used in Syria for study, writing, research, and productivity.",
  },
  summary: {
    ar: "المشكلة الحقيقية ليست في كثرة الأدوات بل في معرفة الأداة المناسبة لكلّ استخدام. يقدّم هذا المقال ترشيحات عملية تغطّي الدردشة العامة، والأدوات القويّة للمهام الطويلة، وأدوات البحث العلمي، وأدوات العروض التقديمية — جميعها مع تركيز على ما يمكن البدء به فورًا دون اشتراك مدفوع.",
    en: "The real problem isn't the number of tools — it's knowing which one fits each real need. This article gives practical recommendations covering general chat, strong long-context tools, research tools, and presentation tools — all focused on what you can start using right now without a paid subscription.",
  },
  metaTitle: {
    ar: "أفضل أدوات الذكاء الاصطناعي المجانية في سوريا | SAS",
    en: "Best Free AI Tools in Syria | SAS",
  },
  metaDescription: {
    ar: "اكتشف أفضل أدوات الذكاء الاصطناعي المجانية في سوريا للطلاب والمبتدئين والباحثين، مع ترشيحات عملية حسب الاستخدام.",
    en: "Discover the best free AI tools in Syria for students, beginners, and researchers, with practical recommendations by use case.",
  },
  keywords: {
    ar: [
      "أفضل أدوات الذكاء الاصطناعي في سوريا",
      "أدوات الذكاء الاصطناعي المجانية",
      "أفضل مواقع الذكاء الاصطناعي المجانية",
      "الذكاء الاصطناعي للطلاب في سوريا",
      "ChatGPT و Gemini في سوريا",
    ],
    en: [
      "best AI tools in Syria",
      "free AI tools",
      "best free AI websites",
      "AI tools for students in Syria",
      "ChatGPT and Gemini in Syria",
    ],
  },
  body: [
    {
      type: "paragraph",
      text: {
        ar: "يبحث كثير من الطلاب في سوريا عن قائمة بأفضل أدوات الذكاء الاصطناعي المجانية، لكن قائمة طويلة وحدها لا تحلّ المشكلة. ما ينقص عادة هو ربط كلّ أداة باستخدامها العملي المناسب. في الأسطر التالية، نعرض ترشيحات موجزة حسب الحاجة الحقيقية — وليس حسب الشهرة.",
        en: "Many students in Syria search for a list of the best free AI tools — but a long list alone doesn't solve the problem. What's usually missing is matching each tool to the real use case it fits. The sections below offer concise recommendations organised by need, not by popularity.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تختار الأداة المناسبة؟", en: "How do you choose the right AI tool?" } },
    {
      type: "paragraph",
      text: {
        ar: "ابدأ من السؤال: ماذا تريد أن تنجز اليوم؟ الإجابات عادة تقع ضمن أربع فئات — محادثة عامة، بحث علمي، تعامل مع ملفات، أو إنتاج عرض تقديمي. كل فئة تتطلّب أداة مختلفة، وأفضل استراتيجية هي اختيار أداة واحدة لكل فئة والالتزام بها.",
        en: "Start from the question: what are you trying to accomplish today? Answers usually fall into four buckets — general chat, research, file handling, or presentations. Each bucket calls for a different tool, and the best strategy is to pick one tool per bucket and stick with it.",
      },
    },
    { type: "heading", level: 2, text: { ar: "الأفضل للمحادثة العامة", en: "Best for general AI help" } },
    {
      type: "paragraph",
      text: {
        ar: "ChatGPT Free وGoogle Gemini وMicrosoft Copilot مناسبة جدًا للبداية. هي مجانية، تدعم اللغة العربية، ومناسبة للشرح والتلخيص والكتابة اليومية. للمهام الأطول أو الملفّات الأكبر، جرّب Kimi أو DeepSeek — فالاثنان يتعاملان مع سياقات طويلة بشكل أفضل.",
        en: "ChatGPT Free, Google Gemini, and Microsoft Copilot are great starting points. They're free, support Arabic, and work well for explaining, summarising, and everyday writing. For longer tasks and larger files, try Kimi or DeepSeek — both handle long context noticeably better.",
      },
    },
    { type: "heading", level: 2, text: { ar: "الأفضل للبحث العلمي", en: "Best for research" } },
    {
      type: "paragraph",
      text: {
        ar: "Perplexity يُعطيك إجابات مباشرة مع روابط للمصادر، ما يجعله مدخلًا ممتازًا للبحث السريع. NotebookLM من Google يساعدك على تنظيم عدة ملفات PDF ومراجع في مساحة واحدة ذكية، وهو عمليّ جدًا لمن يعمل على بحث أكاديمي طويل.",
        en: "Perplexity gives direct answers with links to sources, making it an excellent entry point for fast research. NotebookLM from Google lets you organise several PDFs and references in one smart workspace — very useful for anyone working on a longer academic project.",
      },
    },
    { type: "heading", level: 2, text: { ar: "الأفضل للعروض التقديمية", en: "Best for presentations" } },
    {
      type: "paragraph",
      text: {
        ar: "Gamma يحوّل مسودّة نصّية إلى عرض تقديميّ مرتّب خلال دقائق. لا يحلّ محلّ الفكرة الجيّدة، لكنه يوفّر ساعات من التنسيق، ويترك لك الوقت للتركيز على المحتوى.",
        en: "Gamma turns a written outline into a clean presentation in minutes. It doesn't replace a good idea, but it saves hours of formatting — leaving you time to focus on content.",
      },
    },
    { type: "heading", level: 2, text: { ar: "الأفضل للبرمجة والتحليل", en: "Best for coding and data" } },
    {
      type: "paragraph",
      text: {
        ar: "للبرمجة ومعالجة البيانات، DeepSeek وKimi خياران قويان ضمن الخطة المجانية. ومع أدوات مثل Google Colab يمكن كتابة التعليمات البرمجية وتنفيذها مباشرة من المتصفّح، وهو ما يناسب طلاب البرمجة وعلم البيانات في سوريا.",
        en: "For coding and data, DeepSeek and Kimi are strong choices on free plans. Combined with tools like Google Colab, you can write and run code directly in the browser — a great fit for programming and data students in Syria.",
      },
    },
    {
      type: "takeaway",
      text: {
        ar: "الأداة الأفضل هي التي تستخدمها فعلًا. اختر واحدة لكلّ نوع من مهامك والتزم بها لأسبوعين قبل التوسّع.",
        en: "The best tool is the one you actually use. Pick one for each type of task and stick with it for two weeks before expanding.",
      },
    },
    {
      type: "paragraph",
      text: {
        ar: "يمكنك تصفّح التشكيلة الكاملة في دليل الأدوات المجانية.",
        en: "You can browse the full selection in our free tools directory.",
      },
    },
    {
      type: "link",
      href: "/tools",
      text: { ar: "دليل الأدوات المجانية", en: "Free tools directory" },
      description: {
        ar: "جميع الأدوات المرشّحة في SAS مصنّفة حسب الاستخدام.",
        en: "All SAS-recommended tools, grouped by use case.",
      },
    },
  ],
};

/* Article 3: Academic research / البحث العلمي --------------------- */
const A3 = {
  slug: "ai-for-academic-research-in-syria",
  category: "research",
  date: "2026-02-05",
  dateLabel: { ar: "شباط 2026", en: "February 2026" },
  readingTime: { ar: "6 دقائق", en: "6 min read" },
  coverVariant: "research",
  relatedTools: ["perplexity", "notebooklm", "semantic-scholar", "consensus", "elicit", "chatpdf"],
  title: {
    ar: "كيف يساعد الذكاء الاصطناعي الباحثين والطلاب في سوريا في البحث العلمي؟",
    en: "How Can Artificial Intelligence Help Students and Researchers in Syria with Academic Research?",
  },
  excerpt: {
    ar: "شرح عمليّ لكيفية استخدام الذكاء الاصطناعي في جمع المصادر، قراءة الأوراق، تلخيص المراجع، وتنظيم الملاحظات ضمن البحث العلمي في سوريا.",
    en: "A practical explanation of how AI can support research in Syria through source discovery, paper reading, summarisation, and reference organisation.",
  },
  summary: {
    ar: "أصبح الذكاء الاصطناعي أداة عملية في البحث العلمي، خصوصًا للطلاب والباحثين الذين يتعاملون مع كمّ كبير من الملفات والمراجع. نستعرض هنا كيف تساعدك أدوات مجانية مثل Perplexity وNotebookLM وSemantic Scholar وConsensus على جمع المصادر وقراءة الأوراق واستخلاص النقاط — مع الحفاظ على التفكير النقدي ومسؤولية التحقق من المصادر.",
    en: "AI has become a practical support tool in academic research, especially for students and researchers dealing with many files and references. This piece walks through how free tools such as Perplexity, NotebookLM, Semantic Scholar, and Consensus can help with source discovery, paper reading, and extracting key points — while keeping critical thinking and source verification central.",
  },
  metaTitle: {
    ar: "الذكاء الاصطناعي في البحث العلمي في سوريا | SAS",
    en: "AI for Academic Research in Syria | SAS",
  },
  metaDescription: {
    ar: "تعرّف على كيفية استخدام الذكاء الاصطناعي في البحث العلمي في سوريا من خلال أدوات مجانية تساعد في جمع المصادر وقراءة الملفات وتنظيم المراجع.",
    en: "Learn how artificial intelligence can support academic research in Syria using free tools for sources, PDFs, references, and note organisation.",
  },
  keywords: {
    ar: [
      "الذكاء الاصطناعي في البحث العلمي",
      "البحث العلمي في سوريا",
      "أدوات البحث العلمي بالذكاء الاصطناعي",
      "تلخيص الأبحاث بالذكاء الاصطناعي",
      "قراءة PDF بالذكاء الاصطناعي",
    ],
    en: [
      "AI for academic research",
      "academic research in Syria",
      "AI research tools",
      "summarising papers with AI",
      "reading PDFs with AI",
    ],
  },
  body: [
    {
      type: "paragraph",
      text: {
        ar: "يواجه الباحث الجامعي في سوريا كمًّا كبيرًا من المراجع والملاحظات المتناثرة. أدوات الذكاء الاصطناعي الحديثة تسرّع مرحلتي القراءة والتنظيم، لكنها لا تلغي مسؤولية التحقّق من المصادر ولا تحلّ محلّ التفكير النقدي. فيما يلي خارطة عمليّة للاستخدام.",
        en: "Academic researchers in Syria routinely face a huge volume of references and scattered notes. Modern AI tools speed up the reading and organisation stages — but they don't remove responsibility for source verification and don't replace critical thinking. What follows is a practical map for using them well.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تبدأ البحث بمساعدة الذكاء الاصطناعي؟", en: "How do you start research with AI?" } },
    {
      type: "paragraph",
      text: {
        ar: "ابدأ ببناء صورة عامّة عن الموضوع قبل الغوص في الأوراق الرصينة. Perplexity مفيد هنا لأنّه يقدّم إجابات مباشرة مع روابط المصادر، ما يسمح لك بتكوين فهم أوّلي خلال دقائق ثمّ التوجّه إلى الأوراق الأكاديمية من موقع أقوى.",
        en: "Build a general picture of the topic before diving into rigorous papers. Perplexity is useful here because it gives direct answers with source links — so you form an initial understanding in minutes, then move to academic papers from a stronger footing.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تقرأ ملفات PDF بسرعة؟", en: "How can AI help with PDFs?" } },
    {
      type: "paragraph",
      text: {
        ar: "عند التعامل مع PDF طويل أو عدّة ملفات، تستطيع أدوات مثل ChatPDF وOpenRead أن تجيب على أسئلتك مباشرة من النصّ. لمجموعات أكبر من المراجع، يقدّم NotebookLM مساحة واحدة تجمع كلّ الملفات وتربطها ببعضها مع الحفاظ على الاستشهادات.",
        en: "For a long PDF or several files, tools like ChatPDF and OpenRead can answer your questions directly from the text. For larger sets, NotebookLM gives you a single workspace where all your files sit together and cross-reference each other while keeping citations intact.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تلخّص ورقة علمية؟", en: "How can you summarise papers?" } },
    {
      type: "paragraph",
      text: {
        ar: "Semantic Scholar يمنحك ملخّصًا مختصرًا مع المقاطع المُستشهَد بها، وConsensus يربط السؤال البحثي بدراسات مباشرة. الأداتان مفيدتان بشكل خاص في مراجعة الأدبيات، لأنهما يُعيدانك دائمًا إلى المصدر الأصلي بدلًا من الاكتفاء بالتلخيص.",
        en: "Semantic Scholar offers concise abstracts with the passages they're drawn from, and Consensus connects a research question to relevant studies directly. Both are especially useful for literature reviews because they keep sending you back to the primary source rather than stopping at a summary.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تنظّم الملاحظات والمراجع؟", en: "How can you organise notes and references?" } },
    {
      type: "paragraph",
      text: {
        ar: "الخطوة الأخيرة هي تحويل ما قرأته إلى ملاحظات قصيرة قابلة للاسترجاع، مع ربط كلّ فكرة بالمرجع والسنة. Elicit يساعدك في فرز الأوراق حسب سؤال محدّد، وNotebookLM يُتيح لك بناء ملخّصاتك المخصّصة من مجموعة ملفاتك.",
        en: "The final step is turning what you've read into short, retrievable notes linked back to the reference and year. Elicit helps sort papers against a specific question, and NotebookLM lets you build your own custom summaries from your file set.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما حدود الذكاء الاصطناعي في البحث العلمي؟", en: "What are the limits of AI in academic research?" } },
    {
      type: "paragraph",
      text: {
        ar: "الذكاء الاصطناعي يُسرّع، لكنّه لا يضمن الدقّة. قد يُنتج مراجع غير موجودة أو يسيء تفسير نتيجة دراسة. القاعدة البسيطة: لا تستشهد بأي شيء لم تتحقق منه شخصيًا في المصدر الأصلي.",
        en: "AI accelerates, but doesn't guarantee accuracy. It may produce non-existent references or misrepresent a study's result. The simple rule: don't cite anything you haven't personally verified against the original source.",
      },
    },
    {
      type: "takeaway",
      text: {
        ar: "الذكاء الاصطناعي مساعد بحث، لا باحث. استخدمه لتسريع القراءة والتنظيم، واحتفظ بالتفكير النقدي والتحقق من المصادر في يدك.",
        en: "AI is a research assistant, not a researcher. Use it to speed up reading and organisation — keep critical thinking and source verification in your own hands.",
      },
    },
    {
      type: "paragraph",
      text: {
        ar: "لاستعراض مجموعة أدوات البحث المرشّحة، يمكنك زيارة دليل الأدوات المجانية.",
        en: "To browse the recommended research tools, visit the free tools directory.",
      },
    },
    {
      type: "link",
      href: "/tools",
      text: { ar: "دليل الأدوات المجانية", en: "Free tools directory" },
      description: {
        ar: "أدوات البحث العلمي التي نرشّحها.",
        en: "The research tools we recommend.",
      },
    },
  ],
};

/* Article 4: Learn English with AI / تعلم الإنجليزية بالذكاء الاصطناعي */
const A4 = {
  slug: "learn-english-with-ai-in-syria",
  category: "voice-english",
  date: "2026-02-18",
  dateLabel: { ar: "شباط 2026", en: "February 2026" },
  readingTime: { ar: "5 دقائق", en: "5 min read" },
  coverVariant: "default",
  relatedTools: ["hume", "gliglish", "pi", "character-ai", "chatgpt-free", "google-gemini"],
  title: {
    ar: "تعلّم الإنجليزية بالذكاء الاصطناعي في سوريا بطريقة عملية وبسيطة",
    en: "Learning English with Artificial Intelligence in Syria in a Simple Practical Way",
  },
  excerpt: {
    ar: "مقال يشرح كيف يمكن للطلاب والمبتدئين في سوريا استخدام الذكاء الاصطناعي لتقوية التحدّث والاستماع والمفردات والكتابة باللغة الإنجليزية.",
    en: "A practical article on how students and beginners in Syria can use AI to improve speaking, listening, vocabulary, and writing in English.",
  },
  summary: {
    ar: "يواجه كثير من المتعلّمين في سوريا صعوبة في إيجاد بيئة منتظمة لممارسة الإنجليزية، وهنا يقدّم الذكاء الاصطناعي حلًا عمليًا ومتاحًا. نشرح كيف تساعدك أدوات مثل Hume وGliglish وPi وCharacter.AI على التحدّث، بينما تدعمك ChatGPT وGemini في الكتابة والمفردات — وكيف تبني روتينًا يوميًا بسيطًا من هذين الجانبين.",
    en: "Many learners in Syria struggle to find a consistent environment for speaking practice. AI offers a practical and accessible solution. We walk through how tools like Hume, Gliglish, Pi, and Character.AI help with speaking, while ChatGPT and Gemini support writing and vocabulary — and how to build a simple daily routine from both.",
  },
  metaTitle: {
    ar: "تعلم الإنجليزية بالذكاء الاصطناعي في سوريا | SAS",
    en: "Learning English with AI in Syria | SAS",
  },
  metaDescription: {
    ar: "اكتشف كيف يساعد الذكاء الاصطناعي في تعلّم الإنجليزية في سوريا من خلال أدوات مجانية للمحادثة الصوتية وتحسين الكتابة.",
    en: "Discover how artificial intelligence can support English learning in Syria through free tools for speaking, voice practice, and writing improvement.",
  },
  keywords: {
    ar: [
      "تعلم الإنجليزية بالذكاء الاصطناعي",
      "الذكاء الاصطناعي لتعلم الإنجليزية في سوريا",
      "أفضل أدوات تعلم الإنجليزية بالذكاء الاصطناعي",
      "محادثة إنجليزية بالذكاء الاصطناعي",
      "Hume وGliglish",
    ],
    en: [
      "learning English with AI",
      "AI for learning English in Syria",
      "best AI tools for English speaking",
      "AI conversation tools",
      "Hume and Gliglish",
    ],
  },
  body: [
    {
      type: "paragraph",
      text: {
        ar: "أهمّ ما يحتاجه متعلّم الإنجليزية في سوريا هو بيئة ممارسة منتظمة. تعويض غياب البيئة الطبيعية لم يعد مستحيلًا: أدوات الذكاء الاصطناعي تمنحك اليوم محادثة صوتية حقيقية، وتصحيحًا مكتوبًا فوريًا، وتمارين يومية قصيرة — كلّ ذلك مجانًا.",
        en: "What an English learner in Syria needs most is a consistent practice environment. Making up for the absence of a natural setting is no longer impossible: AI tools now give you real voice conversation, instant written correction, and short daily exercises — all for free.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف يساعد الذكاء الاصطناعي في التحدّث؟", en: "How can AI help with speaking practice?" } },
    {
      type: "paragraph",
      text: {
        ar: "الذكاء الاصطناعي يُزيل أكبر حاجز أمام التحدّث: الخوف من الخطأ أمام الآخرين. يمكنك التمرّن بصوتك لساعات دون ضغط، وتكرار العبارات حتى تتقنها، وطلب تصحيح مباشر للّفظ والقواعد. الهدف هو بناء الثقة قبل بناء الطلاقة.",
        en: "AI removes the biggest barrier to speaking: fear of making mistakes in front of others. You can practise aloud for hours without pressure, repeat phrases until they feel natural, and ask for instant feedback on pronunciation and grammar. The goal is building confidence before fluency.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما أفضل أدوات المحادثة الصوتية؟", en: "Which tools are best for voice-based conversation?" } },
    {
      type: "paragraph",
      text: {
        ar: "Hume هو أقوى خيار نرشّحه للمحادثة الصوتية التفاعلية، إذ يقدّم تجربة طبيعية وأقرب للحوار الحقيقي. Gliglish يركّز على التمرين المنتظم بأسلوب بسيط، Pi يمنحك بيئة حوار هادئة مناسبة للمبتدئين، وCharacter.AI يتيح تدريبًا حواريًا أكثر تنوّعًا.",
        en: "Hume is the strongest choice we recommend for interactive voice conversation — the experience feels natural and close to real dialogue. Gliglish focuses on regular practice in a simple format, Pi offers a calm conversation environment for beginners, and Character.AI opens more varied role-based practice.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تحسّن الكتابة والمفردات؟", en: "How can AI improve writing and vocabulary?" } },
    {
      type: "paragraph",
      text: {
        ar: "استخدم ChatGPT Free أو Google Gemini كمدقّق لغوي شخصي. اكتب جملة أو فقرة وطلب منه إعادة صياغتها بطريقة أبسط أو أكثر دقّة، ثمّ اطلب شرح الفرق. هذه الحلقة القصيرة — كتابة، تصحيح، فهم الفرق — من أسرع الطرق لبناء المفردات.",
        en: "Use ChatGPT Free or Google Gemini as a personal writing coach. Write a sentence or paragraph, ask it to rephrase more simply or more precisely, and then ask it to explain the difference. That short loop — write, correct, understand the difference — is one of the fastest ways to grow vocabulary.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تبني روتينًا يوميًا بسيطًا؟", en: "How can a learner build a simple daily routine?" } },
    {
      type: "paragraph",
      text: {
        ar: "خصّص عشرين دقيقة يوميًا موزّعة هكذا: عشر دقائق محادثة صوتية مع Hume أو Gliglish، وعشر دقائق كتابة وتصحيح مع ChatGPT. واظب على هذا النمط أسبوعين قبل تعديله. الالتزام البسيط المتكرّر أقوى بكثير من الجلسة الطويلة غير المنتظمة.",
        en: "Spend 20 minutes a day split like this: 10 minutes of voice conversation with Hume or Gliglish, and 10 minutes of writing + correction with ChatGPT. Stick with this pattern for two weeks before adjusting. A small, repeated commitment beats a long irregular session every time.",
      },
    },
    {
      type: "takeaway",
      text: {
        ar: "لا تحتاج إلى معلّم دائم أو بيئة خاصة لتتقدّم في الإنجليزية. تحتاج فقط إلى عشرين دقيقة يومية، صوتًا وكتابة، وأداة تثق بها.",
        en: "You don't need a permanent teacher or a special environment to progress in English. You need twenty daily minutes, spoken and written, with a tool you trust.",
      },
    },
    {
      type: "paragraph",
      text: {
        ar: "لاستعراض الأدوات الكاملة للتحدّث والكتابة، زُر دليل الأدوات.",
        en: "Browse the full set of tools for speaking and writing in the tools directory.",
      },
    },
    {
      type: "link",
      href: "/tools",
      text: { ar: "دليل الأدوات المجانية", en: "Free tools directory" },
      description: {
        ar: "أدوات التحدّث والإنجليزية التي نرشّحها.",
        en: "The voice and English tools we recommend.",
      },
    },
  ],
};

/* Article 5: VPN for AI tools / VPN لاستخدام الذكاء الاصطناعي ------ */
const A5 = {
  slug: "vpn-for-ai-tools-in-syria",
  category: "privacy",
  date: "2026-03-02",
  dateLabel: { ar: "آذار 2026", en: "March 2026" },
  readingTime: { ar: "4 دقائق", en: "4 min read" },
  coverVariant: "default",
  relatedTools: ["proton-vpn-free", "windscribe-free"],
  title: {
    ar: "هل تحتاج VPN لاستخدام بعض أدوات الذكاء الاصطناعي في سوريا؟",
    en: "Do You Need a VPN to Use Some AI Tools in Syria?",
  },
  excerpt: {
    ar: "مقال توعويّ يشرح لماذا قد يحتاج بعض المستخدمين في سوريا إلى VPN للوصول إلى بعض أدوات الذكاء الاصطناعي، وكيف يختارون الخيار الأنسب بشكل مسؤول.",
    en: "A simple awareness article explaining why some users in Syria may need a VPN to access certain AI tools and how to choose a practical free option responsibly.",
  },
  summary: {
    ar: "بعض أدوات الذكاء الاصطناعي قد لا تعمل بشكل مستقرّ في جميع الشبكات أو المناطق. نشرح هنا ببساطة ما هو VPN ومتى قد يكون مفيدًا، ونرشّح خيارين مجانيين عمليّين هما Proton VPN Free وWindscribe Free — مع التذكير بأن الهدف تحسين الوصول وحماية الاتصال، لا الاستخدام غير المسؤول.",
    en: "Some AI tools may not work equally well in every network or region. This article explains in simple terms what a VPN is, when it may be useful, and recommends two practical free options — Proton VPN Free and Windscribe Free — while emphasising that the goal is safer, more reliable access, not misuse.",
  },
  metaTitle: {
    ar: "هل تحتاج VPN لاستخدام أدوات الذكاء الاصطناعي في سوريا؟ | SAS",
    en: "Do You Need a VPN to Use AI Tools in Syria? | SAS",
  },
  metaDescription: {
    ar: "تعرّف على دور VPN في الوصول إلى بعض أدوات الذكاء الاصطناعي في سوريا، وكيف تختار خدمة مجانية عملية مثل Proton VPN أو Windscribe.",
    en: "Learn why some AI tools in Syria may require VPN access and how to choose a practical free option such as Proton VPN Free or Windscribe Free.",
  },
  keywords: {
    ar: [
      "VPN في سوريا",
      "استخدام VPN للذكاء الاصطناعي",
      "أفضل VPN مجاني في سوريا",
      "Proton VPN Free",
      "Windscribe Free",
      "الوصول إلى أدوات الذكاء الاصطناعي في سوريا",
    ],
    en: [
      "VPN in Syria",
      "AI tools in Syria",
      "best free VPN in Syria",
      "Proton VPN Free",
      "Windscribe Free",
      "accessing AI tools in Syria",
    ],
  },
  body: [
    {
      type: "paragraph",
      text: {
        ar: "ليست كلّ أدوات الذكاء الاصطناعي متاحة بالشكل نفسه من كلّ بلد أو شبكة. قد تمرّ أحيانًا بتجربة غير مستقرّة أو بصفحة تسجيل دخول ترفض الاستمرار، ولهذا يلجأ بعض المستخدمين في سوريا إلى VPN. في هذا المقال نشرح متى يكون ذلك منطقيًا، وكيف تختار خدمة مجانية مسؤولة.",
        en: "Not every AI tool is equally available from every country or network. You may sometimes hit an unstable experience or a sign-in page that refuses to proceed — and that's why some users in Syria turn to a VPN. This article explains when it makes sense, and how to choose a responsible free service.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما هو VPN؟", en: "What is a VPN?" } },
    {
      type: "paragraph",
      text: {
        ar: "VPN هو اتّصال شبكي مشفّر يمرّ عبر خادم وسيط، فيبدو اتّصالك كما لو أنّه قادم من موقع جغرافي آخر. له هدفان أساسيّان: تحسين الخصوصية على الشبكات العامّة، وتوفير وصول أكثر استقرارًا إلى بعض الخدمات التي قد لا تعمل بشكل جيّد من شبكتك الحالية.",
        en: "A VPN is an encrypted network connection routed through a middle server, so your traffic appears to come from a different location. It has two main purposes: stronger privacy on public networks, and more stable access to some services that may not work well from your current network.",
      },
    },
    { type: "heading", level: 2, text: { ar: "لماذا قد يحتاجه بعض المستخدمين في سوريا؟", en: "Why might some users in Syria need it?" } },
    {
      type: "paragraph",
      text: {
        ar: "بعض أدوات الذكاء الاصطناعي تفرض قيودًا جغرافية على التسجيل أو الاستخدام، أو تعمل بشكل غير مستقرّ على شبكات معيّنة. VPN يوفّر حلًّا عمليًّا لهذه الحالة المحدّدة، دون أن يكون ضرورة لكلّ من يستخدم الذكاء الاصطناعي.",
        en: "Some AI tools apply geographic restrictions on sign-up or usage, or behave unstably on certain networks. A VPN is a practical fix for that specific situation — it isn't a requirement for everyone using AI.",
      },
    },
    { type: "heading", level: 2, text: { ar: "ما أفضل الخيارات المجانية العملية؟", en: "Which practical free options are available?" } },
    {
      type: "paragraph",
      text: {
        ar: "Proton VPN Free هو الخيار الأوّل الذي نرشّحه: خدمة تركّز على الخصوصية، بخطّة مجانية غير محدودة الزمن، تكفي الاستخدام اليومي لمعظم الناس. Windscribe Free خيار ثانٍ جيّد بواجهة سهلة، ومناسب كبديل عندما يكون استخدامك متقطّعًا.",
        en: "Proton VPN Free is the first option we recommend — a privacy-focused service with an unlimited-time free plan that covers the daily needs of most users. Windscribe Free is a good second choice with an easy interface, and works well as an alternative when your usage is intermittent.",
      },
    },
    { type: "heading", level: 2, text: { ar: "كيف تُستخدم VPN بشكل مسؤول؟", en: "How can VPNs be used responsibly?" } },
    {
      type: "paragraph",
      text: {
        ar: "استخدم VPN فقط للأغراض المشروعة: تحسين الخصوصية وحماية الاتصال على الشبكات العامّة، أو الوصول المستقرّ إلى خدمات تعليمية وبرمجية. التزم بالقوانين والسياسات المحلية، ولا تستخدم الخدمة لتجاوز قواعد المنصّات نفسها — هذا قد يعرّض حسابك للإيقاف.",
        en: "Use a VPN only for legitimate purposes: stronger privacy on public networks, or stable access to educational and software services. Respect local laws and policies, and don't use it to violate a platform's own rules — that can get your account banned.",
      },
    },
    {
      type: "takeaway",
      text: {
        ar: "VPN ليس ضرورة لكلّ مستخدم، لكنّه حلّ عمليّ عندما يمنعك الاتّصال من الوصول إلى أداة تحتاجها. اختر خدمة موثوقة مثل Proton VPN Free واستخدمها بمسؤولية.",
        en: "A VPN isn't a must for every user — but it's a practical fix when your connection blocks you from a tool you need. Choose a trusted service like Proton VPN Free and use it responsibly.",
      },
    },
    {
      type: "paragraph",
      text: {
        ar: "للاطلاع على قسم أدوات الخصوصية والوصول الآمن، زُر دليل الأدوات.",
        en: "See the privacy & secure access section of our tools directory for the full list.",
      },
    },
    {
      type: "link",
      href: "/tools",
      text: { ar: "دليل الأدوات المجانية", en: "Free tools directory" },
      description: {
        ar: "قسم الخصوصية والوصول الآمن (VPN).",
        en: "Privacy & secure access (VPN) section.",
      },
    },
  ],
};

export const ARTICLES = [A1, A2, A3, A4, A5];

export const getArticleBySlug = (slug) =>
  ARTICLES.find((a) => a.slug === slug) || null;
